/**
 * MarketingHeader — sticky top-of-page header for every Colony marketing
 * site. Composes the canonical chrome:
 *   - Brand wordmark anchored to the marketing-website root.
 *   - Primary nav links (configurable per app via the `navLinks` prop).
 *   - Optional <EcosystemSwitcher> (cross-product discoverability).
 *   - Optional <LocaleSwitcher> (only renders when ≥ 2 locales configured
 *     on the consumer — LocaleSwitcher itself guards on `languages.length`).
 *   - Optional "Sign in" CTA anchored to the tenant subdomain.
 *   - Mobile menu button (below 1024px) that opens the nav list as a panel hung from the header
 *     row: a WAI-ARIA disclosure — Escape closes it and returns focus to the button; a route
 *     change closes it. The panel and the page-scroll lock behind it are the STYLESHEET's
 *     (shared-styles navigation.css, phones only), so the header writes no inline style anywhere.
 *   - A tenant site (LIFE-SHORTLISTED-001 P10.5) brings a self-describing `brandIdentity` with an
 *     optional LOGO image, CMS menu entries that may leave the site (rendered through
 *     `<SmartLink>`), and a trailing nav item (the storefront's account links).
 *
 * Sticky-scroll: the header listens to window scroll once mounted and
 * toggles `.header-nav-scrolled` after the viewport scrolls past 10px,
 * matching every other Colony marketing-website pattern.
 *
 * Reads brand-assets via `getBrand(appKey)` for the wordmark prefix/suffix
 * + glyph. The consumer's job is to wire `appKey` + `navLinks` + (optional)
 * `localeProps` + (optional) `signInUrl`.
 *
 * @module @puffin-core/marketing-shell/MarketingHeader
 */

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { getBrand } from '@puffin-core/brand-assets';
import { HREF_KIND, classifyHref } from '@puffin-core/block-schema/href';
import { useDisclosure } from '@puffin-core/page-shell/use-disclosure';
import { SmartLink } from '../links/SmartLink.jsx';
import LocaleSwitcher from './LocaleSwitcher.jsx';
import EcosystemSwitcher from './EcosystemSwitcher.jsx';
import { appKeyPropType } from './appKeyPropType.js';
import { useMarketingShellTranslate } from '../hooks/useMarketingShellTranslate.js';

const SCROLL_THRESHOLD_PX = 10;

/**
 * @typedef {Object} NavLinkConfig
 * @property {string} [to]   A site path (a router link) or, for a tenant's CMS menu, any
 *                            destination `<SmartLink>` renders (web, email, phone — same tab).
 *                            Optional ONLY when `submenu` is provided.
 * @property {string} label Visible label (consumer pre-resolves i18n).
 * @property {boolean} [end]  React Router `end` flag (for exact-match active state).
 * @property {boolean} [external]  When true, render as `<a href target=_blank
 *                                  rel="noopener noreferrer">` instead of `<NavLink>`.
 *                                  Phase O.2 (pos-web): supports off-domain nav links
 *                                  such as the blog subdomain.
 * @property {readonly NavLinkConfig[]} [submenu]  PROTECTOR-MKT-002: When present,
 *                                  renders as a WAI-ARIA disclosure button + hidden
 *                                  <ul> of child nav links. Click toggles the submenu;
 *                                  Escape closes it (returning focus to the trigger);
 *                                  outside-click closes it; selecting a child link
 *                                  closes it (route change handled by NavLink).
 *                                  NOT a `menubar` (disclosure is the canonical
 *                                  pattern for navigation submenus per WAI-ARIA APG).
 */

/**
 * @typedef {Object} LocaleConfig
 * @property {Array<{code, label}>} languages
 * @property {string} storageKey
 * @property {Object} [storage] - the site's ONE StorageService instance (omitted on the server)
 * @property {string} STORAGE_TYPE_LOCAL
 */

/**
 * useHeaderChrome — owns the header's transient chrome state: the sticky-scroll flag and the mobile
 * menu (a `useDisclosure`), with the side effects that hang off them. Extracted so
 * <MarketingHeader> stays a pure composition of brand + nav + CTA slots rather than a state machine
 * with a render attached.
 *
 * The menu listens for Escape only while it is open (the `NavSubmenuItem` pattern), so Escape
 * pressed anywhere else on the page is never taken from its owner. It locks nothing: the page
 * behind an open menu is held by the stylesheet on phones only, so a menu left open while the
 * window widens past 1024px cannot freeze a desktop page.
 *
 * @param {string} pathname  Current router pathname. The mobile menu closes whenever it changes.
 * @returns {{isScrolled: boolean, mobileOpen: boolean, toggleMobile: Function,
 *   menuButtonRef: {current: ?HTMLButtonElement}}}
 */
function useHeaderChrome(pathname) {
  const [isScrolled, setIsScrolled] = useState(false);
  const { isOpen: mobileOpen, toggle: toggleMobile, close: closeMobile } = useDisclosure();
  const menuButtonRef = useRef(null);

  // Close the mobile menu whenever the route changes.
  useEffect(() => {
    closeMobile();
  }, [pathname, closeMobile]);

  // Scroll-detection: toggle the .header-nav-scrolled class once the
  // viewport has scrolled past SCROLL_THRESHOLD_PX. Listener mounted
  // once on mount; the closure captures only the setter so no stale-state.
  useEffect(() => {
    // No `typeof window === 'undefined'` guard: useEffect callbacks only run
    // client-side (never during SSR), so `window` is always defined here.
    const onScroll = () => {
      setIsScrolled(window.scrollY > SCROLL_THRESHOLD_PX);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Escape closes the open menu and puts focus back on its button.
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeMobile();
        // No `menuButtonRef.current` guard: this handler is registered only while the menu is
        // open, and the button that opened it is always rendered, so the ref is never null here.
        menuButtonRef.current.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mobileOpen, closeMobile]);

  return { isScrolled, mobileOpen, toggleMobile, menuButtonRef };
}

/**
 * HeaderBrand — the brand anchor, linked to the site root and named by `aria-label`. A tenant's LOGO
 * image replaces the wordmark and falls back to it when the image fails to load; otherwise the
 * optional glyph <img> + the two-tone brand-name span pair. Extracted to module scope so the
 * header's own JSX stays shallow.
 */
function HeaderBrand({ appName, glyphSrc, logoSrc, wordmarkPrefix, wordmarkSuffix }) {
  const [logoFailed, setLogoFailed] = useState(false);
  if (logoSrc && !logoFailed) {
    return (
      <NavLink to="/" className="header-logo" aria-label={appName}>
        {/* No width/height attributes: the logo is TENANT-SUPPLIED at runtime, so its aspect ratio
            is unknowable at build time and fixed attributes would distort every logo that does not
            match. `.header-logo-image` pins the height and lets the width follow, so nothing shifts
            vertically. The alt is empty because the link's `aria-label` already names the brand. */}
        <img
          src={logoSrc}
          alt=""
          className="header-logo-image"
          loading="eager"
          onError={() => setLogoFailed(true)}
        />
      </NavLink>
    );
  }
  return (
    <NavLink to="/" className="header-logo" aria-label={appName}>
      {glyphSrc ? (
        <img src={glyphSrc} alt="" width="32" height="32" loading="eager" aria-hidden="true" />
      ) : null}
      <span className="brand-name">
        <span className="brand-name-prefix">{wordmarkPrefix}</span>
        <span className="brand-name-suffix">{wordmarkSuffix}</span>
      </span>
    </NavLink>
  );
}

HeaderBrand.propTypes = {
  appName: PropTypes.string.isRequired,
  glyphSrc: PropTypes.string,
  logoSrc: PropTypes.string,
  wordmarkPrefix: PropTypes.string.isRequired,
  wordmarkSuffix: PropTypes.string.isRequired,
};

/**
 * HeaderNavItem — one entry of the nav list. A `submenu` is a disclosure; `external` (a Colony
 * marketing site's off-domain link, e.g. its blog subdomain) opens a new tab; a site path is a router
 * `<NavLink>` that marks the current page; anything else — a tenant's CMS entry for a newsletter,
 * a shop, an email address — renders through `<SmartLink>`: the same tab, a safe `rel`, an
 * outbound report, and plain text for a URL that is not a destination.
 */
function HeaderNavItem({ link, onOutboundClick }) {
  if (link.submenu) {
    return <NavSubmenuItem link={link} />;
  }
  if (link.external) {
    return (
      <li className="header-nav-item">
        <a href={link.to} className="header-nav-link" target="_blank" rel="noopener noreferrer">
          {link.label}
        </a>
      </li>
    );
  }
  if (classifyHref(link.to) === HREF_KIND.INTERNAL) {
    return (
      <li className="header-nav-item">
        <NavLink
          to={link.to}
          end={link.end}
          className={({ isActive }) =>
            isActive ? 'header-nav-link header-nav-link-active' : 'header-nav-link'
          }
        >
          {link.label}
        </NavLink>
      </li>
    );
  }
  return (
    <li className="header-nav-item">
      <SmartLink href={link.to} className="header-nav-link" onOutboundClick={onOutboundClick}>
        {link.label}
      </SmartLink>
    </li>
  );
}

HeaderNavItem.propTypes = {
  link: PropTypes.shape({
    to: PropTypes.string,
    label: PropTypes.string.isRequired,
    end: PropTypes.bool,
    external: PropTypes.bool,
    submenu: PropTypes.array,
  }).isRequired,
  onOutboundClick: PropTypes.func,
};

/**
 * @param {Object} props
 * @param {string} [props.appKey]               brand-assets key (e.g. 'parcel'); required unless `brandIdentity`
 *                                              describes the brand (`appKeyPropType`).
 * @param {readonly NavLinkConfig[]} props.navLinks
 * @param {LocaleConfig} [props.localeProps]    If present, render LocaleSwitcher.
 * @param {boolean} [props.showEcosystemSwitcher=true]
 * @param {{label, href}} [props.signInCta]     Optional primary CTA (typically "Sign in"
 *                                              or "Start Free Trial").
 * @param {{label, href}} [props.secondaryCta]  Optional secondary CTA rendered alongside
 *                                              the primary. Phase O.2 (pos-web): supports
 *                                              dual-CTA headers such as Login + Start Trial.
 * @param {readonly string[]} [props.hideCtasOnPaths=[]]  Path prefixes where both primary +
 *                                              secondary CTAs are hidden. Useful for
 *                                              multi-step wizard routes (e.g. /signup/wizard)
 *                                              where the header CTA would compete with the
 *                                              wizard's own "Continue" button. Matching is by
 *                                              prefix — `'/signup/wizard'` also hides on
 *                                              `'/signup/wizard/step-2'`.
 *
 * The shell's own words — the menu button's open / close labels, the switchers' labels — come from
 * marketing-shell's `marketingShell` namespace, never from props (LIFE-SHORTLISTED-001 F-Q7-1: nine
 * sites passed the same words through, and the tenant site a third copy).
 * @param {{appName:string,wordmarkPrefix:string,wordmarkSuffix:string,glyphSrc?:string,logoSrc?:string}} [props.brandIdentity]
 *   BLOG-VANITY-SSR: self-describing white-label brand chrome. When provided, the
 *   wordmark + glyph come from here instead of `getBrand(appKey)` (used by vanity
 *   blog appKeys served off a shared deploy / absent from brand-assets), and `appKey` may be
 *   omitted. `logoSrc` (a tenant's logo) replaces the wordmark while it loads.
 * @param {(href: string, destinationKind: string) => void} [props.onOutboundClick]
 *   Reports a click on a nav entry that leaves the site (see `<SmartLink>`).
 * @param {React.ReactNode} [props.trailingNavItem]  Rendered as the LAST nav list item — a tenant
 *   storefront's account links.
 * @param {string} [props.navLabel]  Names the header's navigation landmark (`aria-label`) — needed
 *   once the page has other navigation landmarks, as a tenant site's footer does. Unnamed without.
 */
export default function MarketingHeader({
  appKey,
  navLinks,
  localeProps,
  showEcosystemSwitcher = true,
  signInCta,
  secondaryCta,
  hideCtasOnPaths = [],
  brandIdentity,
  onOutboundClick,
  trailingNavItem,
  navLabel,
}) {
  // BLOG-VANITY-SSR: a self-describing `brandIdentity` (white-label vanity blog)
  // overrides the brand-assets lookup, so the shell never calls `getBrand` for an
  // appKey the registry doesn't carry (customcarvers) and never renders the host
  // deploy's own glyph PNG on a vanity domain. Absent → `getBrand(appKey)` (the 9
  // ParcelPuffin-family apps, unchanged). `getBrand` is only called on the fallback path.
  const brand = brandIdentity || getBrand(appKey);
  // A vanity brandIdentity may omit `glyphSrc` (no first-party glyph asset on the
  // shared deploy) — render the wordmark text only, skipping the <img>.
  const glyphSrc = brandIdentity ? brandIdentity.glyphSrc : brand.assets.glyph;
  const location = useLocation();
  // Multilingual content engine — blog-path-aware navigation: pick a
  // locale on a `/blog…` route and the LocaleSwitcher rewrites the URL
  // via the host SPA's router (vs. a full-page `window.location.assign`).
  const navigate = useNavigate();
  const { isScrolled, mobileOpen, toggleMobile, menuButtonRef } = useHeaderChrome(
    location.pathname,
  );
  const t = useMarketingShellTranslate();

  // Context-aware CTA suppression: hide CTAs whenever the current pathname
  // starts with any configured prefix. Prefix-match so deep routes under
  // a wizard root (/signup/wizard/step-2) inherit the suppression.
  const ctasHidden = hideCtasOnPaths.some((p) => location.pathname.startsWith(p));

  const headerClassName = [
    'header-portal',
    isScrolled ? 'header-nav-scrolled' : null,
    mobileOpen ? 'header-mobile-nav-open' : null,
  ]
    .filter(Boolean)
    .join(' ');

  // Pre-resolved trailing nav slots. Each is a whole <li> or nothing — holding
  // them as named consts keeps the returned tree a flat list of slots instead of
  // four inline ternaries stacked inside the <ul>.
  const ecosystemItem = showEcosystemSwitcher ? (
    <li className="header-nav-item">
      <EcosystemSwitcher currentAppKey={appKey} />
    </li>
  ) : null;

  const localeItem = localeProps ? (
    <li className="header-nav-item">
      <LocaleSwitcher
        languages={localeProps.languages}
        storageKey={localeProps.storageKey}
        storage={localeProps.storage}
        STORAGE_TYPE_LOCAL={localeProps.STORAGE_TYPE_LOCAL}
        initialLocale={localeProps.initialLocale}
        defaultLocale={localeProps.defaultLocale}
        onChange={localeProps.onChange}
        navigate={navigate}
      />
    </li>
  ) : null;

  const secondaryCtaItem =
    secondaryCta && !ctasHidden ? (
      <li className="header-nav-item">
        <a className="btn btn-marketing-secondary" href={secondaryCta.href}>
          {secondaryCta.label}
        </a>
      </li>
    ) : null;

  const trailingItem = trailingNavItem ? (
    <li className="header-nav-item">{trailingNavItem}</li>
  ) : null;

  const signInCtaItem =
    signInCta && !ctasHidden ? (
      <li className="header-nav-item">
        <a className="btn btn-marketing-primary" href={signInCta.href}>
          {signInCta.label}
        </a>
      </li>
    ) : null;

  return (
    <header className={headerClassName} role="banner">
      <div className="header-content">
        <HeaderBrand
          appName={brand.appName}
          glyphSrc={glyphSrc}
          logoSrc={brandIdentity ? brandIdentity.logoSrc : undefined}
          wordmarkPrefix={brand.wordmarkPrefix}
          wordmarkSuffix={brand.wordmarkSuffix}
        />

        <button
          ref={menuButtonRef}
          type="button"
          className={mobileOpen ? 'header-menu-btn header-menu-btn-open' : 'header-menu-btn'}
          onClick={toggleMobile}
          aria-expanded={mobileOpen}
          aria-controls="header-nav-list-menu"
          aria-label={mobileOpen ? t('menu.close') : t('menu.open')}
        >
          <span aria-hidden="true" className="header-menu-btn-bar" />
          <span aria-hidden="true" className="header-menu-btn-bar" />
          <span aria-hidden="true" className="header-menu-btn-bar" />
        </button>

        <nav aria-label={navLabel}>
          <ul id="header-nav-list-menu" className="header-nav-list">
            {navLinks.map((link) => (
              <HeaderNavItem
                key={link.submenu ? `submenu:${link.label}` : link.to}
                link={link}
                onOutboundClick={onOutboundClick}
              />
            ))}
            {ecosystemItem}
            {localeItem}
            {secondaryCtaItem}
            {signInCtaItem}
            {trailingItem}
          </ul>
        </nav>
      </div>
    </header>
  );
}

/**
 * NavSubmenuItem — disclosure-pattern trigger + collapsible <ul> for a
 * single nav link with `submenu` children. Implements the WAI-ARIA APG
 * disclosure pattern (NOT menubar):
 *   - <button aria-expanded aria-controls> toggles a hidden <ul>.
 *   - ESC closes + returns focus to the trigger.
 *   - mousedown outside the submenu wrapper closes it.
 *   - Clicking a child link closes it (NavLink also fires the route change).
 *
 * PROTECTOR-MKT-002 (2026-05-24).
 */
function NavSubmenuItem({ link }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        // No `triggerRef.current` guard: this handler is registered only while
        // `open` is true, and the trigger <button ref={triggerRef}> is always
        // rendered when the submenu is open, so the ref is never null here.
        triggerRef.current.focus();
      }
    };
    const onMouseDown = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [open]);

  const toggle = useCallback(() => {
    setOpen((o) => !o);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  // `useId` is a custom-group hook as far as hook-ordering goes (it is not one
  // of the state/effect/computed built-ins), so it sorts last. Its value is read
  // only in the returned JSX — nothing above depends on it.
  const submenuId = useId();

  return (
    <li className="header-nav-item header-nav-item-submenu" ref={wrapperRef}>
      <button
        ref={triggerRef}
        type="button"
        className="header-nav-link header-submenu-trigger"
        aria-expanded={open}
        aria-controls={submenuId}
        onClick={toggle}
      >
        {link.label}
      </button>
      <ul id={submenuId} className="header-submenu-list" hidden={!open}>
        {link.submenu.map((child) =>
          child.external ? (
            <li key={child.to} className="header-submenu-item">
              <a
                className="header-submenu-link"
                href={child.to}
                target="_blank"
                rel="noopener noreferrer"
                onClick={close}
              >
                {child.label}
              </a>
            </li>
          ) : (
            <li key={child.to} className="header-submenu-item">
              <NavLink
                className={({ isActive }) =>
                  isActive
                    ? 'header-submenu-link header-submenu-link-active'
                    : 'header-submenu-link'
                }
                to={child.to}
                end={child.end}
                onClick={close}
              >
                {child.label}
              </NavLink>
            </li>
          ),
        )}
      </ul>
    </li>
  );
}

NavSubmenuItem.propTypes = {
  link: PropTypes.shape({
    label: PropTypes.string.isRequired,
    submenu: PropTypes.arrayOf(
      PropTypes.shape({
        to: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        end: PropTypes.bool,
        external: PropTypes.bool,
      }),
    ).isRequired,
  }).isRequired,
};

MarketingHeader.propTypes = {
  appKey: appKeyPropType,
  navLinks: PropTypes.arrayOf(
    PropTypes.shape({
      to: PropTypes.string,
      label: PropTypes.string.isRequired,
      end: PropTypes.bool,
      external: PropTypes.bool,
      submenu: PropTypes.array,
    }),
  ).isRequired,
  localeProps: PropTypes.shape({
    languages: PropTypes.array.isRequired,
    storageKey: PropTypes.string.isRequired,
    // The site's StorageService INSTANCE: optional in the prerender harness (server) but
    // required for the client browser path; the LocaleSwitcher itself
    // guards the storage calls.
    storage: PropTypes.shape({ get: PropTypes.func.isRequired, set: PropTypes.func.isRequired }),
    STORAGE_TYPE_LOCAL: PropTypes.string.isRequired,
    initialLocale: PropTypes.string,
    defaultLocale: PropTypes.string,
    onChange: PropTypes.func,
  }),
  showEcosystemSwitcher: PropTypes.bool,
  signInCta: PropTypes.shape({
    label: PropTypes.string.isRequired,
    href: PropTypes.string.isRequired,
  }),
  secondaryCta: PropTypes.shape({
    label: PropTypes.string.isRequired,
    href: PropTypes.string.isRequired,
  }),
  hideCtasOnPaths: PropTypes.arrayOf(PropTypes.string),
  // BLOG-VANITY-SSR: self-describing white-label brand chrome (overrides getBrand).
  brandIdentity: PropTypes.shape({
    appName: PropTypes.string.isRequired,
    wordmarkPrefix: PropTypes.string.isRequired,
    wordmarkSuffix: PropTypes.string.isRequired,
    glyphSrc: PropTypes.string,
    logoSrc: PropTypes.string,
  }),
  onOutboundClick: PropTypes.func,
  trailingNavItem: PropTypes.node,
  navLabel: PropTypes.string,
};
